import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { debounce } from "lodash";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, MapPin, Car, Tag, X, Wallet, Plus, Minus, ShoppingCart, Trash2, CreditCard, Ship, Truck, Bike, Calendar, AlertCircle, AlertTriangle, Clock, Sparkles, Check, Package } from "lucide-react";
import { type CreateOrderDto, type CartItem, type Service, type AddOnQuantities, getAddOnQuantity } from "@/lib/types";
import { useCreateOrder } from "@/hooks/use-orders";
import { useServices } from "@/hooks/use-services";
import { useGovernorates, useDistricts, useDeliveryFee, useAvailableTimeSlots, useTimeSlotsForServices } from "@/hooks/use-locations";
import { getCurrentUser, useProfile } from "@/hooks/use-auth";
import { useAddresses, useCreateAddress } from "@/hooks/use-addresses";
import { useWalletBalance } from "@/hooks/use-wallet";
import { useShopCart } from "@/lib/cart";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MobileSelect,
  MobileSelectItem,
} from "@/components/ui/mobile-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { getReorderItems } from "@/lib/reorder";
import { MapPicker } from "@/components/MapPicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/components/ThemeProvider";
import { SetPreferencesDialog } from "@/components/SetPreferencesDialog";
import { BookingShopSheet } from "@/components/BookingShopSheet";

// Localize a "h:mm AM/PM" time string; converts the period to ص/م for Arabic.
function localizeTime(time: string, isArabic: boolean): string {
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return time;
  const hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3].toUpperCase();
  const mm = minutes.toString().padStart(2, '0');
  return isArabic
    ? `${hours}:${mm} ${period === 'AM' ? 'ص' : 'م'}`
    : `${hours}:${mm} ${period}`;
}

// Compute end time (1 hour later) from a "h:mm AM/PM" time string.
function computeEndTime(time: string): string {
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return time;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const isPM = match[3].toUpperCase() === 'PM';
  if (isPM && hours !== 12) hours += 12;
  if (!isPM && hours === 12) hours = 0;
  const totalMinutes = hours * 60 + minutes + 60;
  let endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  const endPeriod = endHours >= 12 ? 'PM' : 'AM';
  if (endHours > 12) endHours -= 12;
  if (endHours === 0) endHours = 12;
  return `${endHours}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
}

// Upper bound on how many of a single add-on can be taken; mirrors the
// server's MAX_ADD_ON_QUANTITY.
const MAX_ADD_ON_QUANTITY = 99;

// Narrow a quantity map to the currently selected add-ons, dropping the
// implicit ones so the payload only carries real choices.
function pickAddOnQuantities(
  selectedIds: string[],
  quantities: AddOnQuantities,
): AddOnQuantities | undefined {
  const picked: AddOnQuantities = {};
  for (const id of selectedIds) {
    const quantity = quantities[id];
    if (quantity && quantity > 1) picked[id] = quantity;
  }
  return Object.keys(picked).length > 0 ? picked : undefined;
}

// Cars may share one cart line only when service, car type and add-ons (with
// their quantities) all match; another car with different add-ons gets its
// own line. Per-unit lines are never merged.
function isSameCarSetup(
  item: CartItem,
  candidate: Pick<CartItem, "serviceId" | "vehicleType" | "addOns" | "addOnQuantities">,
): boolean {
  if (item.isPerUnitPricing) return false;
  if (item.serviceId !== candidate.serviceId || item.vehicleType !== candidate.vehicleType) return false;
  const mine = [...(item.addOns ?? [])].sort();
  const theirs = [...(candidate.addOns ?? [])].sort();
  if (mine.length !== theirs.length || mine.some((id, i) => id !== theirs[i])) return false;
  return mine.every(
    (id) =>
      getAddOnQuantity(item.addOnQuantities, id) ===
      getAddOnQuantity(candidate.addOnQuantities, id),
  );
}

// Forget an add-on's quantity (used when it is deselected).
function dropAddOnQuantity(quantities: AddOnQuantities, addOnId: string): AddOnQuantities {
  if (quantities[addOnId] === undefined) return quantities;
  const { [addOnId]: _removed, ...rest } = quantities;
  return rest;
}

// Step an add-on's quantity within [1, MAX_ADD_ON_QUANTITY].
function stepAddOnQuantity(
  quantities: AddOnQuantities,
  addOnId: string,
  delta: number,
): AddOnQuantities {
  const next = Math.min(
    MAX_ADD_ON_QUANTITY,
    Math.max(1, getAddOnQuantity(quantities, addOnId) + delta),
  );
  return { ...quantities, [addOnId]: next };
}

// Quantity stepper shown on add-ons the admin marked with allowQuantity. Lives
// inside clickable add-on rows, so it swallows clicks that would toggle them.
function AddOnQuantityStepper({
  quantity,
  onChange,
  onSelect,
  onDeselect,
  testId,
}: {
  // 0 means the add-on is not selected — the stepper stays usable so the
  // customer can add it straight from here.
  quantity: number;
  onChange: (delta: number) => void;
  // Called when stepping 0 -> 1, i.e. the plus button adds the add-on.
  onSelect?: () => void;
  // Called when stepping 1 -> 0, i.e. the minus button removes the add-on.
  // Omit to keep minus disabled at 1 instead.
  onDeselect?: () => void;
  testId?: string;
}) {
  return (
    <div
      className="flex items-center gap-1 shrink-0"
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 rounded-full"
        disabled={quantity <= 0 || (quantity <= 1 && !onDeselect)}
        onClick={(e) => {
          e.stopPropagation();
          if (quantity <= 1 && onDeselect) {
            onDeselect();
            return;
          }
          onChange(-1);
        }}
        aria-label="decrease"
      >
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-6 text-center text-sm font-medium tabular-nums">{quantity}</span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 rounded-full"
        disabled={quantity >= MAX_ADD_ON_QUANTITY}
        onClick={(e) => {
          e.stopPropagation();
          if (quantity <= 0 && onSelect) {
            onSelect();
            return;
          }
          onChange(1);
        }}
        aria-label="increase"
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

export default function Booking() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const isRtl = i18n.language === 'ar';
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [bookingLat, setBookingLat] = useState<number | undefined>(undefined);
  const [bookingLng, setBookingLng] = useState<number | undefined>(undefined);
  const [couponCode, setCouponCode] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [useWalletCredit, setUseWalletCredit] = useState(false);
  // Shop-cart products can ride along with the wash (delivered by the crew).
  const shopCart = useShopCart();
  const [includeShopCart, setIncludeShopCart] = useState(false);
  // In-page shop drawer — lets the customer add products without leaving
  // (and losing) the booking they are filling in.
  const [shopSheetOpen, setShopSheetOpen] = useState(false);
  const [customWalletAmount, setCustomWalletAmount] = useState<string>("");

  // Cart state for multi-service orders
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [addVehicleType, setAddVehicleType] = useState<'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat' | null>(null);

  // Vehicle selection dialog for URL-based service add
  const [pendingService, setPendingService] = useState<Service | null>(null);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);

  // Per-unit pricing state (caravan/boat)
  const [pendingUnitService, setPendingUnitService] = useState<Service | null>(null);
  const [selectedFeet, setSelectedFeet] = useState<number>(25);

  // Time slot availability warning
  const [showTimeSlotWarning, setShowTimeSlotWarning] = useState(false);
  const [hasAvailableSlots, setHasAvailableSlots] = useState(true);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  // Order-wide global add-ons (scope 'order') — applies once to the whole order.
  const [orderAddOns, setOrderAddOns] = useState<string[]>([]);
  // Item-scoped add-on dialog: when a service is selected and global item
  // add-ons exist, this dialog lets the customer pick which to attach.
  const [pendingItemService, setPendingItemService] = useState<CartItem | null>(null);
  // Index of the cart line whose add-ons are being edited; null = adding a new one.
  const [editingCartIndex, setEditingCartIndex] = useState<number | null>(null);
  const [pendingItemAddOns, setPendingItemAddOns] = useState<string[]>([]);
  // How many of each selected add-on, kept per selection surface. Add-ons
  // without allowQuantity never get an entry, so they always count as one.
  const [selectedAddOnQty, setSelectedAddOnQty] = useState<AddOnQuantities>({});
  const [orderAddOnQty, setOrderAddOnQty] = useState<AddOnQuantities>({});
  const [pendingItemAddOnQty, setPendingItemAddOnQty] = useState<AddOnQuantities>({});
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showBlockedDialog, setShowBlockedDialog] = useState(false);
  const [showAddServiceDialog, setShowAddServiceDialog] = useState(false);
  const [expandedCartIndex, setExpandedCartIndex] = useState<number | null>(null);
  // Tracks whether the preferences prompt has been dismissed this session, so
  // closing it doesn't immediately re-open (derived from live profile data).
  const [preferencesDismissed, setPreferencesDismissed] = useState(false);

  const currentUser = getCurrentUser();
  const { data: profileUser, refetch: refetchProfile } = useProfile();
  const { data: savedAddresses } = useAddresses();

  // Ensure the profile (and its `preferencesSet` flag) is fresh when the user
  // lands on booking, so old customers are prompted reliably. React Query may
  // serve cached data first; this forces a background refetch on mount.
  useEffect(() => {
    refetchProfile();
  }, [refetchProfile]);

  // Prompt old customers (registered before the preferences feature) to set
  // their preferences. Uses the backend `preferencesSet` flag (reliable — it
  // bypasses Mongoose hydration defaults). Derived directly from the profile
  // query so it stays correct even if the cached profile updates after mount.
  const showPreferencesDialog =
    !preferencesDismissed &&
    !!profileUser &&
    profileUser.preferencesSet === false;
  const { data: walletData } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;


  // Helper function to format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0
        ? `${hours} ${t("booking.hours")} ${mins} ${t("services.minsEstimated")}`
        : `${hours} ${t("booking.hours")}`;
    }
    return `${minutes} ${t("services.minsEstimated")}`;
  };

  // Helper function to get next N days
  const getNextDays = (days: number): Date[] => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const arabicDayNames: Record<string, string> = {
    'Sun': 'الأحد',
    'Mon': 'الاثنين',
    'Tue': 'الثلاثاء',
    'Wed': 'الأربعاء',
    'Thu': 'الخميس',
    'Fri': 'الجمعة',
    'Sat': 'السبت'
  };

  const getDayName = (date: Date): string => {
    const enDayName = format(date, 'EEE', { locale: enUS });
    return isRtl ? arabicDayNames[enDayName] || enDayName : enDayName;
  };

  const formSchema = z.object({
    customerAddress: z.string().optional(),
    governorateId: z.string().min(1, t("booking.validation.selectGovernorate")),
    districtId: z.string().min(1, t("booking.validation.selectDistrict")),
    appointmentDate: z.date({
      required_error: t("booking.validation.dateRequired"),
    }),
    timeSlotId: z.string().min(1, t("booking.validation.timeRequired")),
    paymentMethod: z.enum(['knet', 'credit_card', 'apple_pay', 'wallet', 'online'], {
      required_error: t("booking.validation.paymentRequired"),
    }),
    notes: z.string().optional(),
    block: z.string().optional(),
    avenue: z.string().optional(),
    street: z.string().optional(),
    building: z.string().optional(),
    landmark: z.string().optional(),
    saveAddress: z.boolean().optional(),
    addressLabel: z.string().optional()
  });

  const { data: services, isLoading: servicesLoading } = useServices();
  const { data: governorates, isLoading: governoratesLoading } = useGovernorates();
  const { mutate: createOrder, isPending } = useCreateOrder();
  const { mutate: createAddress } = useCreateAddress();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customerName: currentUser?.name || "",
      customerAddress: "",
      governorateId: "",
      districtId: "",
      timeSlotId: "",
      paymentMethod: 'online',
      notes: "",
      block: "",
      avenue: "",
      street: "",
      building: "",
      landmark: "",
      saveAddress: false,
      addressLabel: "",
    },
  });

  const selectedGovernorateId = form.watch("governorateId");
  const selectedDistrictId = form.watch("districtId");
  const selectedDate = form.watch("appointmentDate");
  const prevGovernorateRef = useRef<string>("");
  const dateTimeSectionRef = useRef<HTMLDivElement>(null);

  // Live-build customerAddress for manual entry so the order summary shows the full address.
  const block = form.watch("block");
  const avenue = form.watch("avenue");
  const street = form.watch("street");
  const building = form.watch("building");
  const landmark = form.watch("landmark");

  // A usable address is either a saved one, or the required manual fields
  // (street + house/building) being filled — needed before the order summary.
  const addressReady = Boolean(
    selectedAddressId || (street?.trim() && building?.trim())
  );
  useEffect(() => {
    if (!useManualAddress) return;
    const parts = [
      block && `${t("addresses.block")} ${block}`,
      street && `${t("addresses.street")} ${street}`,
      avenue && `${t("addresses.avenue")} ${avenue}`,
      building && `${t("addresses.building")} ${building}`,
      landmark,
    ].filter(Boolean);
    form.setValue("customerAddress", parts.join(", "), { shouldValidate: false });
  }, [block, avenue, street, building, landmark, useManualAddress, t, form]);

  // Get available vehicle types based on services with pricing > 0
  const availableVehicleTypes = useMemo(() => {
    if (!services || services.length === 0) {
      return ['sedan']; // Default fallback
    }
    const nonAddOns = services.filter(s => !s.isAddOn);
    const available: Array<'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat'> = [];

    const checkType = (type: 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat') => {
      return nonAddOns.some(s =>
        s.vehiclePricing &&
        s.vehiclePricing[type] &&
        s.vehiclePricing[type] > 0
      );
    };

    if (checkType('sedan')) available.push('sedan');
    if (checkType('suv')) available.push('suv');
    if (checkType('cuv')) available.push('cuv');
    if (checkType('van')) available.push('van');
    if (checkType('motorcycle')) available.push('motorcycle');
    if (checkType('caravan')) available.push('caravan');
    if (checkType('boat')) available.push('boat');

    return available.length > 0 ? available : ['sedan'];
  }, [services]);

  const formattedDate = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;

  const { data: districts, isLoading: districtsLoading } = useDistricts(selectedGovernorateId);
  const { data: deliveryFeeData } = useDeliveryFee(selectedDistrictId);

  const { data: timeSlots, isLoading: timeSlotsLoading } = useAvailableTimeSlots(formattedDate);

  // Get available slots based on current cart items (for showing unavailable slots differently)
  const itemsForAvailability = useMemo(() => {
    return cartItems.map(item => ({
      serviceId: item.serviceId,
      quantity: item.quantity,
      ...(item.unit !== undefined && { unit: item.unit }),
      ...(item.addOns && item.addOns.length > 0 && { addOns: item.addOns }),
      ...(item.addOnQuantities && { addOnQuantities: item.addOnQuantities })
    }));
  }, [cartItems]);

  const { data: availableSlotsForServices } = useTimeSlotsForServices(formattedDate, itemsForAvailability);

  const availableSlotIds = useMemo(() => {
    return new Set(availableSlotsForServices?.map(slot => slot.id) || []);
  }, [availableSlotsForServices]);

  // Debounced function to check if selected time slot is still available after cart changes
  const checkTimeSlotAvailability = debounce(async () => {
    const selectedTimeSlotId = form.watch("timeSlotId");
    if (!formattedDate || !selectedTimeSlotId || cartItems.length === 0) {
      return;
    }

    const items = cartItems.map(item => ({
      serviceId: item.serviceId,
      quantity: item.quantity,
      ...(item.unit !== undefined && { unit: item.unit }),
      ...(item.addOns && item.addOns.length > 0 && { addOns: item.addOns }),
      ...(item.addOnQuantities && { addOnQuantities: item.addOnQuantities })
    }));

    try {
      setIsCheckingAvailability(true);
      const response = await apiClient.post('/time-slots/available-for-services', {
        date: formattedDate,
        items
      });

      if (!response.ok) {
        throw new Error('Failed to check availability');
      }

      const availableSlots = await response.json();
      const isAvailable = availableSlots.some((slot: { id: string }) => slot.id === selectedTimeSlotId);
      const hasAnySlots = availableSlots.length > 0;

      if (!isAvailable) {
        setShowTimeSlotWarning(true);
        setHasAvailableSlots(hasAnySlots);
      } else {
        setShowTimeSlotWarning(false);
        setHasAvailableSlots(true);
      }
    } catch (error) {
      console.error('Error checking time slot availability:', error);
    } finally {
      setIsCheckingAvailability(false);
    }
  }, 500);

  // Trigger availability check when cart items or date changes
  useEffect(() => {
    checkTimeSlotAvailability();
  }, [cartItems, formattedDate]);

  // Reset selected vehicle type whenever the cart becomes empty
  useEffect(() => {
    if (cartItems.length === 0) setAddVehicleType(null);
  }, [cartItems.length]);

  useEffect(() => {
    if (useManualAddress && selectedGovernorateId && prevGovernorateRef.current && prevGovernorateRef.current !== selectedGovernorateId) {
      form.setValue("districtId", "");
    }
    prevGovernorateRef.current = selectedGovernorateId;
  }, [selectedGovernorateId, form, useManualAddress]);

  useEffect(() => {
    if (savedAddresses && savedAddresses.length > 0 && !selectedAddressId && !useManualAddress) {
      const defaultAddr = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
        form.setValue("governorateId", defaultAddr.governorateId);
        form.setValue("districtId", defaultAddr.districtId);
        const addrStr = [
          defaultAddr.block && `${t("addresses.block")} ${defaultAddr.block}`,
          defaultAddr.street && `${t("addresses.street")} ${defaultAddr.street}`,
          defaultAddr.avenue && `${t("addresses.avenue")} ${defaultAddr.avenue}`,
          defaultAddr.building && `${t("addresses.building")} ${defaultAddr.building}`,
          defaultAddr.landmark
        ].filter(Boolean).join(", ");
        form.setValue("customerAddress", addrStr);
      }
    }
  }, [savedAddresses, selectedAddressId, useManualAddress, form, t]);

  // Auto-add service from URL query parameter
  useEffect(() => {
    // 1. Re-order: if items were staged in sessionStorage, load them directly.
    if (cartItems.length === 0 && !pendingService && !pendingUnitService) {
      const reorderItems = getReorderItems();
      if (reorderItems && reorderItems.length > 0) {
        setCartItems(reorderItems);
        // Set the add-vehicle type to the first item's vehicle so the service
        // picker is in a sensible state.
        if (reorderItems[0].vehicleType) {
          setAddVehicleType(reorderItems[0].vehicleType);
        }
        return; // skip URL-prefill logic
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const serviceId = urlParams.get('serviceId');
    const giftCode = urlParams.get('gift');
    const vehicleParam = urlParams.get('vehicle') as 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'caravan' | 'boat' | null;

    if (serviceId && services && services.length > 0 && cartItems.length === 0 && !pendingService && !pendingUnitService) {
      const service = services.find(s => s.id === serviceId);
      if (service) {
        // Clear the URL parameter first
        window.history.replaceState({}, '', '/booking');

        // Check if service is per-unit pricing (caravan/boat)
        if (service.isPerUnitPricing) {
          setPendingUnitService(service);
          setShowUnitDialog(true);
          return;
        }

        // If vehicle type is specified in URL, use it directly
        if (vehicleParam && service.vehiclePricing && service.vehiclePricing[vehicleParam]) {
          const unitPrice = service.vehiclePricing[vehicleParam];
          const newItem: CartItem = {
            serviceId: service.id,
            vehicleType: vehicleParam,
            quantity: 1,
            serviceName: service.nameEn,
            serviceNameAr: service.nameAr,
            unitPrice: Number(unitPrice),
            subtotal: Number(unitPrice),
            duration: service.duration || 0
          };
          setCartItems([newItem]);
          return;
        }

        // Check if service has multiple vehicle prices and no vehicle was pre-selected
        const hasMultiplePrices = service.vehiclePricing &&
          Object.values(service.vehiclePricing).filter(p => p && p > 0).length > 1;

        if (hasMultiplePrices) {
          // Show dialog to ask for vehicle type
          setPendingService(service);
          setShowVehicleDialog(true);
        } else {
          // Auto-add with default (first available price or base price)
          let unitPrice = service.price;
          let vehicleType: 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'caravan' | 'boat' = 'sedan';

          if (service.vehiclePricing) {
            const priceEntries = Object.entries(service.vehiclePricing).filter(([_, p]) => p && p > 0);
            if (priceEntries.length > 0) {
              vehicleType = priceEntries[0][0] as any;
              unitPrice = priceEntries[0][1];
            }
          }

          const newItem: CartItem = {
            serviceId: service.id,
            vehicleType,
            quantity: 1,
            serviceName: service.nameEn,
            serviceNameAr: service.nameAr,
            unitPrice: Number(unitPrice),
            subtotal: Number(unitPrice),
            duration: service.duration || 0
          };
          setCartItems([newItem]);
        }
      }
    }
  }, [services, cartItems.length, pendingService, pendingUnitService]);

  // Handle vehicle type selection from dialog
  const handleVehicleSelect = (vehicleType: 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat') => {
    if (pendingService) {
      const unitPrice = pendingService.vehiclePricing && pendingService.vehiclePricing[vehicleType]
        ? pendingService.vehiclePricing[vehicleType]
        : pendingService.price;

      const newItem: CartItem = {
        serviceId: pendingService.id,
        vehicleType,
        quantity: 1,
        serviceName: pendingService.nameEn,
        serviceNameAr: pendingService.nameAr,
        unitPrice,
        subtotal: unitPrice,
        duration: pendingService.duration || 0
      };
      setCartItems([newItem]);
      setPendingService(null);
      setShowVehicleDialog(false);
    }
  };

  const handleAddressSelect = (addressId: string) => {
    const addr = savedAddresses?.find(a => a.id === addressId);
    if (addr) {
      setSelectedAddressId(addressId);
      setUseManualAddress(false);
      form.setValue("governorateId", addr.governorateId);
      form.setValue("districtId", addr.districtId);
      const addrStr = [
        addr.block && `${t("addresses.block")} ${addr.block}`,
        addr.street && `${t("addresses.street")} ${addr.street}`,
        addr.avenue && `${t("addresses.avenue")} ${addr.avenue}`,
        addr.building && `${t("addresses.building")} ${addr.building}`,
        addr.landmark
      ].filter(Boolean).join(", ");
      form.setValue("customerAddress", addrStr);
      form.setValue("saveAddress", false);
    setBookingLat(undefined);
    setBookingLng(undefined);
      form.setValue("addressLabel", "");
    }
  };

  const handleManualAddressToggle = () => {
    setUseManualAddress(true);
    setSelectedAddressId(null);
    form.setValue("governorateId", "");
    form.setValue("districtId", "");
    form.setValue("customerAddress", "");
    form.setValue("block", "");
    form.setValue("avenue", "");
    form.setValue("street", "");
    form.setValue("building", "");
    form.setValue("landmark", "");
    form.setValue("saveAddress", false);
    setBookingLat(undefined);
    setBookingLng(undefined);
    form.setValue("addressLabel", "");
  };

  const getServicePrice = (service: Service | undefined, vehicleType: 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat') => {
    if (!service) return 0;
    // Per-unit pricing shows price per feet
    if (service.isPerUnitPricing) {
      return service.price;
    }
    if (service.vehiclePricing) {
      if (vehicleType === 'sedan') return service.vehiclePricing.sedan;
      if (vehicleType === 'suv') return service.vehiclePricing.suv;
      if (vehicleType === 'cuv' && service.vehiclePricing.cuv) return service.vehiclePricing.cuv;
      if (vehicleType === 'motorcycle' && service.vehiclePricing.motorcycle) return service.vehiclePricing.motorcycle;
      if (vehicleType === 'van' && service.vehiclePricing.van) return service.vehiclePricing.van;
      // Fallback for CUV and motorcycle if specific pricing not available
      if (vehicleType === 'cuv' || vehicleType === 'motorcycle') return service.vehiclePricing.suv;
      return service.vehiclePricing.sedan;
    }
    return service.price;
  };

  // Calculate price for per-unit services
  const calculatePerUnitPrice = (service: Service, feet: number): number => {
    if (service.isPerUnitPricing) {
      return service.price * feet;
    }
    return service.price;
  };

  // Calculate duration for per-unit services based on time ranges
  const calculatePerUnitDuration = (service: Service, feet: number): number => {
    if (service.isPerUnitPricing && service.timeRanges && service.timeRanges.length > 0) {
      const range = service.timeRanges.find(
        r => feet >= r.minFeet && feet <= r.maxFeet
      );
      return range?.duration || service.duration;
    }
    return service.duration;
  };

  // Get available add-on services
  const availableAddOnServices = useMemo(() => {
    if (!pendingUnitService?.availableAddOns || !services) return [];
    return services.filter(s =>
      s.isAddOn && pendingUnitService.availableAddOns?.includes(s.id)
    );
  }, [pendingUnitService, services]);

  // Unit price of an add-on for a vehicle — mirrors the server rule
  // (orders.service resolveAddOnPrice): a truthy vehiclePricing entry wins,
  // otherwise the base price. Order-scoped add-ons pass no vehicle.
  const getAddOnPrice = useCallback((addOn: Service, vehicleType?: string): number => {
    const vehiclePrice = vehicleType
      ? (addOn.vehiclePricing as Record<string, number | undefined> | undefined)?.[vehicleType]
      : undefined;
    return vehiclePrice || addOn.price || 0;
  }, []);

  // Sum an add-on field across a selection, weighted by how many of each the
  // customer took. Add-ons without allowQuantity always count once.
  const sumAddOns = useCallback(
    (ids: string[], quantities: AddOnQuantities, field: 'price' | 'duration', vehicleType?: string) =>
      ids.reduce((sum, id) => {
        const addOn = services?.find(s => s.id === id);
        if (!addOn) return sum;
        const unit = field === 'price' ? getAddOnPrice(addOn, vehicleType) : (addOn.duration || 0);
        return sum + unit * getAddOnQuantity(quantities, id);
      }, 0),
    [services, getAddOnPrice],
  );

  // Vehicle type of the per-unit service being configured (boat/caravan
  // dialog) — needed so its add-ons price by vehicle like everything else.
  const pendingUnitVehicleType: 'caravan' | 'boat' | undefined = pendingUnitService
    ? (pendingUnitService.category?.toLowerCase().includes('boat') ? 'boat' : 'caravan')
    : undefined;

  // Calculate total add-ons price
  const addOnsTotalPrice = useMemo(
    () => sumAddOns(selectedAddOns, selectedAddOnQty, 'price', pendingUnitVehicleType),
    [selectedAddOns, selectedAddOnQty, sumAddOns, pendingUnitVehicleType],
  );

  // Calculate total add-ons duration
  const addOnsTotalDuration = useMemo(
    () => sumAddOns(selectedAddOns, selectedAddOnQty, 'duration'),
    [selectedAddOns, selectedAddOnQty, sumAddOns],
  );

  // Global add-ons available to all services, split by scope. Item-scoped ones
  // attach to a cart item; order-scoped ones apply once to the whole order.
  const globalItemAddOns = useMemo(
    () => (services || []).filter(s => s.isGlobalAddOn && s.addOnScope === 'item' && s.isActive),
    [services],
  );
  const globalOrderAddOns = useMemo(
    () => (services || []).filter(s => s.isGlobalAddOn && s.addOnScope === 'order' && s.isActive),
    [services],
  );

  /**
   * Add-ons offered for a single cart item: the globally available item-scoped
   * ones PLUS any the admin linked to that specific service via
   * `availableAddOns`. The item dialog used to list only the global ones, so
   * per-service add-ons were configured in the dashboard but never shown.
   */
  const getItemAddOnsFor = useCallback(
    (serviceId?: string) => {
      const linkedIds = services?.find(s => s.id === serviceId)?.availableAddOns ?? [];
      const merged = [...globalItemAddOns];
      for (const addOn of services || []) {
        if (!addOn.isAddOn || !addOn.isActive) continue;
        if (!linkedIds.includes(addOn.id)) continue;
        if (merged.some(m => m.id === addOn.id)) continue;   // already global
        merged.push(addOn);
      }
      return merged;
    },
    [services, globalItemAddOns],
  );

  // Total price of the selected order-wide add-ons.
  const orderAddOnsTotal = useMemo(
    () => sumAddOns(orderAddOns, orderAddOnQty, 'price'),
    [orderAddOns, orderAddOnQty, sumAddOns],
  );

  // Add item to cart directly when service is selected
  const handleServiceSelect = useCallback((serviceId: string) => {
    if (!serviceId) return;

    const service = services?.find(s => s.id === serviceId);
    if (!service) return;

    // Handle per-unit pricing services (caravan/boat)
    if (service.isPerUnitPricing) {
      setPendingUnitService(service);
      setSelectedFeet(service.minUnit || 25);
      setSelectedAddOns([]);
      setSelectedAddOnQty({});
      setShowUnitDialog(true);
      return;
    }

    // Regular services require a vehicle type to be selected
    if (!addVehicleType) return;

    const unitPrice = getServicePrice(service, addVehicleType);
    const newItem: CartItem = {
      serviceId: serviceId,
      vehicleType: addVehicleType,
      quantity: 1,
      serviceName: service.nameEn,
      serviceNameAr: service.nameAr,
      unitPrice,
      subtotal: unitPrice,
      duration: service.duration
    };

    // If this service has any attachable add-ons — global or linked to it
    // specifically — let the customer pick them first. That holds even when
    // the same service and car type are already in the cart: another car may
    // want different add-ons. confirmItemAddOns folds it into an identical line.
    if (getItemAddOnsFor(serviceId).length > 0) {
      setPendingItemService(newItem);
      setPendingItemAddOns([]);
      setPendingItemAddOnQty({});
      return;
    }

    // No add-ons to choose: one more car of the same kind joins its line.
    const existingIndex = cartItems.findIndex(item => isSameCarSetup(item, newItem));
    if (existingIndex >= 0) {
      setCartItems(cartItems.map((item, i) =>
        i === existingIndex
          ? { ...item, quantity: item.quantity + 1, subtotal: item.unitPrice * (item.quantity + 1) }
          : item,
      ));
    } else {
      setCartItems([...cartItems, newItem]);
    }
  }, [addVehicleType, services, cartItems, getItemAddOnsFor]);

  /**
   * Reopen the add-on dialog for a line already in the cart, seeded with what
   * the customer picked last time so they can change their mind.
   */
  const editCartItemAddOns = useCallback((index: number) => {
    const item = cartItems[index];
    if (!item) return;
    setEditingCartIndex(index);
    setPendingItemService(item);
    setPendingItemAddOns([...(item.addOns ?? [])]);
    setPendingItemAddOnQty({ ...(item.addOnQuantities ?? {}) });
  }, [cartItems]);

  // Confirm the item-scoped add-on dialog. Adds the pending item to the cart,
  // or — when reopened from an existing line — updates that line in place.
  const confirmItemAddOns = useCallback(() => {
    if (!pendingItemService) return;
    const addOnPrice = sumAddOns(pendingItemAddOns, pendingItemAddOnQty, 'price', pendingItemService.vehicleType);
    const addOnDuration = sumAddOns(pendingItemAddOns, pendingItemAddOnQty, 'duration');
    // `duration` on a cart line already includes the previous add-ons, so take
    // the base from the service definition rather than compounding it.
    const baseDuration =
      services?.find(s => s.id === pendingItemService.serviceId)?.duration
      ?? pendingItemService.duration
      ?? 0;
    const quantity = pendingItemService.quantity || 1;

    const item: CartItem = {
      ...pendingItemService,
      subtotal: ((pendingItemService.unitPrice || 0) + addOnPrice) * quantity,
      duration: baseDuration + addOnDuration,
      addOns: pendingItemAddOns.length > 0 ? [...pendingItemAddOns] : undefined,
      addOnQuantities:
        pendingItemAddOns.length > 0
          ? pickAddOnQuantities(pendingItemAddOns, pendingItemAddOnQty)
          : undefined,
    };

    setCartItems(prev => {
      if (editingCartIndex !== null) {
        return prev.map((existing, i) => (i === editingCartIndex ? item : existing));
      }
      // A new car with exactly the same add-ons as a line already in the cart
      // joins that line; any other choice keeps its own line.
      const twin = prev.findIndex(existing => isSameCarSetup(existing, item));
      if (twin < 0) return [...prev, item];
      return prev.map((existing, i) =>
        i === twin
          ? { ...existing, quantity: existing.quantity + quantity, subtotal: existing.subtotal + item.subtotal }
          : existing,
      );
    });
    setEditingCartIndex(null);
    setPendingItemService(null);
    setPendingItemAddOns([]);
    setPendingItemAddOnQty({});
  }, [pendingItemService, pendingItemAddOns, pendingItemAddOnQty, sumAddOns, services, editingCartIndex]);

  // Add-on dialog subtitle: say when this is another car of a kind already in
  // the cart, or when an edit covers several cars on one line.
  const itemAddOnsHint = (() => {
    if (!pendingItemService) return t("booking.itemAddOnsDesc");
    if (editingCartIndex !== null) {
      return pendingItemService.quantity > 1
        ? t("booking.editAddOnsAllCars", { count: pendingItemService.quantity })
        : t("booking.itemAddOnsDesc");
    }
    const anotherCar = cartItems.some(
      (item) =>
        !item.isPerUnitPricing &&
        item.serviceId === pendingItemService.serviceId &&
        item.vehicleType === pendingItemService.vehicleType,
    );
    return t(anotherCar ? "booking.anotherCarAddOnsDesc" : "booking.itemAddOnsDesc");
  })();

  // Add per-unit service to cart with selected feet and add-ons
  const addPerUnitServiceToCart = useCallback(() => {
    if (!pendingUnitService) return;

    const basePrice = calculatePerUnitPrice(pendingUnitService, selectedFeet);
    const totalPrice = basePrice + addOnsTotalPrice;
    const baseDuration = calculatePerUnitDuration(pendingUnitService, selectedFeet);
    const totalDuration = baseDuration + addOnsTotalDuration;

    // Determine vehicle type based on service category
    const vehicleType: 'caravan' | 'boat' = pendingUnitVehicleType ?? 'caravan';

    const newItem: CartItem = {
      serviceId: pendingUnitService.id,
      vehicleType,
      quantity: 1,
      unit: selectedFeet,
      addOns: selectedAddOns.length > 0 ? selectedAddOns : undefined,
      addOnQuantities:
        selectedAddOns.length > 0
          ? pickAddOnQuantities(selectedAddOns, selectedAddOnQty)
          : undefined,
      serviceName: pendingUnitService.nameEn,
      serviceNameAr: pendingUnitService.nameAr,
      unitPrice: totalPrice,
      subtotal: totalPrice,
      isPerUnitPricing: true,
      duration: totalDuration
    };

    setCartItems([...cartItems, newItem]);
    setPendingUnitService(null);
    setShowUnitDialog(false);
    setSelectedAddOns([]);
    setSelectedAddOnQty({});
  }, [pendingUnitService, pendingUnitVehicleType, selectedFeet, selectedAddOns, selectedAddOnQty, addOnsTotalPrice, addOnsTotalDuration, cartItems]);

  // Toggle add-on selection
  const toggleAddOn = useCallback((addOnId: string) => {
    setSelectedAddOns(prev =>
      prev.includes(addOnId)
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
    // Deselecting drops the quantity so re-picking it starts back at one.
    setSelectedAddOnQty(prev => dropAddOnQuantity(prev, addOnId));
  }, []);

  // Update cart item quantity
  const updateCartItemQuantity = useCallback((index: number, delta: number) => {
    const updated = [...cartItems];
    const item = { ...updated[index] };
    item.quantity = Math.max(1, item.quantity + delta);
    // Add-ons scale with the line quantity. This used to be unitPrice*quantity,
    // which silently dropped the add-on charge as soon as the customer changed
    // the quantity. Per-unit lines already fold add-ons into unitPrice.
    const addOnPrice = item.isPerUnitPricing
      ? 0
      : sumAddOns(item.addOns ?? [], item.addOnQuantities ?? {}, 'price', item.vehicleType);
    item.subtotal = (item.unitPrice + addOnPrice) * item.quantity;
    updated[index] = item;
    setCartItems(updated);
  }, [cartItems, sumAddOns]);

  // "+" on a line means one more car. When the service has add-ons, ask which
  // ones this car gets (pre-filled with the line's) rather than copying them:
  // the same add-ons join the line, different ones start a line of their own.
  const addAnotherCar = useCallback((index: number) => {
    const item = cartItems[index];
    if (!item) return;
    if (item.isPerUnitPricing || getItemAddOnsFor(item.serviceId).length === 0) {
      updateCartItemQuantity(index, 1);
      return;
    }
    setEditingCartIndex(null);
    setPendingItemService({ ...item, quantity: 1, subtotal: item.unitPrice });
    setPendingItemAddOns([...(item.addOns ?? [])]);
    setPendingItemAddOnQty({ ...(item.addOnQuantities ?? {}) });
  }, [cartItems, getItemAddOnsFor, updateCartItemQuantity]);

  // Remove item from cart
  const removeFromCart = useCallback((index: number) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  }, [cartItems]);

  // Calculate totals
  const cartSubtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  }, [cartItems]);

  const deliveryFee = deliveryFeeData?.deliveryFee || 0;
  // Order-wide global add-ons add to the subtotal once.
  const subtotalWithAddOns = cartSubtotal + orderAddOnsTotal;
  const subtotalWithDelivery = subtotalWithAddOns + deliveryFee;
  // Attached shop products are always full price — coupons apply to the
  // service portion only (mirrors the server's create()).
  const shopProductsTotal = includeShopCart && shopCart.items.length > 0 ? shopCart.subtotal : 0;
  const totalPrice = Math.max(0, subtotalWithDelivery - couponDiscount) + shopProductsTotal;

  const maxWalletCredit = Math.min(walletBalance, totalPrice);
  const parsedCustomAmount = parseFloat(customWalletAmount) || 0;
  const walletCreditUsed = useWalletCredit
    ? (customWalletAmount.trim() !== ""
      ? Math.min(Math.max(0, parsedCustomAmount), maxWalletCredit)
      : maxWalletCredit)
    : 0;
  const cashAmountDue = totalPrice - walletCreditUsed;

  // Clear coupon when cart or location changes
  useEffect(() => {
    if (couponApplied) {
      setCouponCode("");
      setCouponInput("");
      setCouponDiscount(0);
      setCouponError(null);
      setCouponApplied(false);
    }
  }, [cartItems, selectedDistrictId]);

  // The customer never picks a gateway here: 'wallet' when the wallet covers
  // the whole order, otherwise 'online' and Confirm Booking sends them straight
  // to the UPayments page where they choose KNET / card / Apple Pay.
  useEffect(() => {
    const next = cashAmountDue === 0 && useWalletCredit ? 'wallet' : 'online';
    if (form.getValues("paymentMethod") !== next) {
      form.setValue("paymentMethod", next);
    }
  }, [cashAmountDue, useWalletCredit, form]);

  const validateCoupon = useCallback(async () => {
    if (!couponInput.trim() || cartItems.length === 0) return;

    setCouponValidating(true);
    setCouponError(null);

    // Get all unique service IDs for coupon validation
    const serviceIds = [...new Set(cartItems.map(item => item.serviceId))];

    // Build orderItems for service-specific discount calculation
    const orderItems = cartItems.map(item => ({
      serviceId: item.serviceId,
      subtotal: item.subtotal
    }));

    try {
      const response = await apiClient.post("/coupons/validate", {
        code: couponInput.trim().toUpperCase(),
        orderAmount: subtotalWithDelivery,
        serviceIds: serviceIds,
        governorateId: selectedGovernorateId,
        orderItems: orderItems
      });

      const result = await response.json();

      if (result.valid) {
        setCouponCode(couponInput.trim().toUpperCase());
        setCouponDiscount(result.discount);
        setCouponApplied(true);
        setCouponError(null);
      } else {
        setCouponError(result.message || t("booking.coupon.invalid"));
        setCouponApplied(false);
        setCouponDiscount(0);
      }
    } catch {
      setCouponError(t("booking.coupon.invalid"));
      setCouponApplied(false);
      setCouponDiscount(0);
    } finally {
      setCouponValidating(false);
    }
  }, [couponInput, subtotalWithDelivery, cartItems, selectedGovernorateId, t]);

  const removeCoupon = useCallback(() => {
    setCouponCode("");
    setCouponInput("");
    setCouponDiscount(0);
    setCouponError(null);
    setCouponApplied(false);
  }, []);

  function onSubmit(data: z.infer<typeof formSchema>) {
    // Step-by-step validation: check each step in order and scroll to the
    // first incomplete one so the customer knows what's missing.
    const scrollToStep = (selector: string) => {
      document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Step 1: Date + Time
    if (!selectedDate) {
      toast({ title: t("booking.validation.dateRequired"), variant: "destructive", duration: 2000 });
      dateTimeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (!data.timeSlotId) {
      toast({ title: t("booking.validation.timeRequired"), variant: "destructive", duration: 2000 });
      dateTimeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Step 2: Services (cart must not be empty)
    if (cartItems.length === 0) {
      toast({ title: t("booking.validation.serviceRequired"), variant: "destructive", duration: 2000 });
      scrollToStep('[data-step="services"]');
      return;
    }

    // Step 3: Location
    const isManualMode = useManualAddress || !savedAddresses || savedAddresses.length === 0;
    if (!isManualMode && !selectedAddressId) {
      toast({ title: t("booking.validation.addressRequired"), variant: "destructive", duration: 2000 });
      scrollToStep('[data-step="location"]');
      return;
    }

    if (isManualMode) {
      if (!data.block || data.block.trim() === "") {
        form.setError("block", { message: t("addresses.validation.blockRequired") });
        toast({
          title: t("booking.validation.blockRequired"),
          description: t("addresses.validation.blockRequired"),
          variant: "destructive",
          duration: 2000,
        });
        document.querySelector('[data-testid="input-block"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!data.street || data.street.trim() === "") {
        form.setError("street", { message: t("addresses.validation.streetRequired") });
        toast({
          title: t("booking.validation.streetRequired"),
          description: t("addresses.validation.streetRequired"),
          variant: "destructive",
          duration: 2000,
        });
        document.querySelector('[data-testid="input-street"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!data.building || data.building.trim() === "") {
        form.setError("building", { message: t("addresses.validation.buildingRequired") });
        toast({
          title: t("booking.validation.buildingRequired"),
          description: t("addresses.validation.buildingRequired"),
          variant: "destructive",
          duration: 2000,
        });
        document.querySelector('[data-testid="input-building"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    const orderData: CreateOrderDto = {
      items: cartItems.map(item => ({
        serviceId: item.serviceId,
        vehicleType: item.vehicleType,
        quantity: item.quantity,
        ...(item.unit !== undefined && { unit: item.unit }),
        ...(item.addOns && item.addOns.length > 0 && { addOns: item.addOns }),
        ...(item.addOnQuantities && { addOnQuantities: item.addOnQuantities }),
      })),
      timeSlotId: data.timeSlotId,
      governorateId: data.governorateId,
      districtId: data.districtId,
      customerName: data.customerName,
      notes: data.notes,
      paymentMethod: data.paymentMethod,
      ...(orderAddOns.length > 0
        ? {
            addOns: orderAddOns,
            ...(pickAddOnQuantities(orderAddOns, orderAddOnQty)
              ? { addOnQuantities: pickAddOnQuantities(orderAddOns, orderAddOnQty) }
              : {}),
          }
        : {}),
      ...(couponApplied && couponCode ? { couponCode } : {}),
      ...(includeShopCart && shopCart.items.length > 0
        ? {
            cartProducts: shopCart.items.map((i) => ({
              productId: i.productId,
              quantity: i.quantity,
            })),
            ...(shopCart.b2bKey ? { b2bKey: shopCart.b2bKey } : {}),
          }
        : {}),
      ...(useWalletCredit ? {
        useWalletCredit: true,
        walletCreditAmount: walletCreditUsed
      } : {})
    };

    if (selectedAddressId && !useManualAddress) {
      orderData.addressId = selectedAddressId;
      const addr = savedAddresses?.find(a => a.id === selectedAddressId);
      if (addr) {
        orderData.governorateId = addr.governorateId;
        orderData.districtId = addr.districtId;
      }
    } else {
      orderData.block = data.block;
      orderData.avenue = data.avenue;
      orderData.street = data.street;
      orderData.building = data.building;
      orderData.landmark = data.landmark;
      const customerAddress = `Block ${data.block}${data.avenue ? `, Ave ${data.avenue}` : ''}, Street ${data.street}, Building ${data.building}${data.landmark ? `, ${data.landmark}` : ''}`;
      orderData.customerAddress = customerAddress;
      // Include location if set via map
      if (bookingLat) orderData.latitude = bookingLat;
      if (bookingLng) orderData.longitude = bookingLng;
    }

    createOrder(orderData, {
      onSuccess: async (order) => {
        // The shop cart is now part of this order — don't leave it hanging.
        if (includeShopCart && shopCart.items.length > 0) {
          shopCart.clearCart();
        }

        // Save address if needed before redirecting
        if (isManualMode && data.saveAddress && data.block && data.street && data.building && data.governorateId && data.districtId) {
          const district = districts?.find(d => d.id === data.districtId);
          const defaultLabel = district ? `${isRtl ? district.nameAr : district.nameEn}` : t("addresses.defaultLabel");

          createAddress({
            label: data.addressLabel?.trim() || defaultLabel,
            governorateId: data.governorateId,
            districtId: data.districtId,
            block: data.block.trim(),
            avenue: data.avenue?.trim(),
            street: data.street.trim(),
            building: data.building.trim(),
            landmark: data.landmark?.trim() || undefined,
            isDefault: !savedAddresses || savedAddresses.length === 0,
            latitude: bookingLat,
            longitude: bookingLng,
          });
        }

        // Mark gift as used if booking came from a gift redemption
        const giftCode = sessionStorage.getItem("giftBooking");
        if (giftCode) {
          const giftData = JSON.parse(giftCode);
          try {
            await apiClient.post("/gifts/redeem", { code: giftData.code });
          } catch {
            // Gift redemption may fail if already marked — not critical
          }
          sessionStorage.removeItem("giftBooking");
        }

        // Check payment scenario
        // Scenario 1: Full wallet payment - no gateway redirect needed
        if (order.cashAmountDue === 0 && (order.walletCreditUsed ?? 0) > 0) {
          // Order is already confirmed and paid with wallet credit
          window.location.href = `/payment/success?orderId=${order.id}`;
        }
        // Scenario 2 & 3: Partial or no wallet - redirect to payment gateway
        else if (order.paymentLink) {
          window.location.href = order.paymentLink;
        }
      },
      onError: (error: Error) => {
        // Check if error is due to deactivated account (403 Forbidden)
        if (error.message.includes('deactivated') || error.message.includes('Forbidden')) {
          setShowBlockedDialog(true);
        }
        // Check if error is due to time slot being fully booked (400 Bad Request)
        else if (error.message.includes('fully booked')) {
          toast({
            title: t("booking.toast.timeSlotFullyBooked.title"),
            description: t("booking.toast.timeSlotFullyBooked.description"),
            variant: "destructive",
          });
          // Refresh time slots to show updated availability
          queryClient.invalidateQueries({ queryKey: ['/time-slots/available-for-services'] });
        }
        else {
          toast({
            title: t("booking.toast.error.title"),
            description: error.message || t("booking.toast.error.description"),
            variant: "destructive",
          });
        }
      }
    });
  }

  const SectionHeader = ({ icon: Icon, title, step, totalSteps = 4 }: { icon: typeof Car; title: string; step: number; totalSteps?: number }) => (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <h3 className="font-semibold text-lg">{title}</h3>
      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{step}/{totalSteps}</span>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-32 lg:pb-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 font-display">{t("booking.title")}</h1>
            <p className="text-muted-foreground">
              {t("booking.subtitle")}
            </p>
          </div>

          <div className="bg-card rounded-2xl p-6 shadow-lg border border-border/50">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

                {/* Step 1: Date & Time */}
                <div ref={dateTimeSectionRef}>
                  <SectionHeader icon={Calendar} title={t("booking.stepDateTime")} step={1} />

                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="appointmentDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t("booking.preferredDate")}</FormLabel>
                          <FormControl>
                            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                              {getNextDays(14).map((date, index) => {
                                const isSelected = field.value && format(date, 'yyyy-MM-dd') === format(field.value, 'yyyy-MM-dd');
                                const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                                const dayName = isToday ? t('booking.today') : getDayName(date);
                                const dayNumber = format(date, 'd');

                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() => field.onChange(date)}
                                    className={cn(
                                      "flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-xl border-2 min-w-[70px] transition-all",
                                      isSelected
                                        ? "border-primary bg-primary/10 text-white"
                                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                                    )}
                                  >
                                    <span className="text-xs font-medium">{dayName}</span>
                                    <span className="text-lg font-semibold">{dayNumber}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="timeSlotId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">{t("booking.preferredTime")}</FormLabel>
                          <FormControl>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                              {!selectedDate ? (
                                <div className="col-span-full text-center text-sm text-muted-foreground py-4">
                                  {t("booking.selectDateFirst")}
                                </div>
                              ) : timeSlotsLoading ? (
                                <>
                                  {[1, 2, 3, 4, 5].map((i) => (
                                    <div
                                      key={i}
                                      className="flex flex-col items-center justify-center p-3 rounded-xl border-2 animate-pulse bg-muted"
                                    >
                                      <div className="h-4 w-12 bg-muted-foreground/20 rounded mb-1"></div>
                                      <div className="h-3 w-10 bg-muted-foreground/20 rounded"></div>
                                    </div>
                                  ))}
                                </>
                              ) : (timeSlots || []).length === 0 ? (
                                <div className="col-span-full text-center text-sm text-muted-foreground py-4">
                                  {t("booking.noTimeSlots")}
                                </div>
                              ) : (
                                (timeSlots || []).map((slot) => {
                                  const isSelected = field.value === slot.id;
                                  const isUnavailable = !isCheckingAvailability && cartItems.length > 0 && !availableSlotIds.has(slot.id);
                                  const displayEndTime = computeEndTime(slot.time);
                                  return (
                                    <button
                                      key={slot.id}
                                      type="button"
                                      onClick={() => !isUnavailable && (setShowTimeSlotWarning(false), field.onChange(slot.id))}
                                      disabled={isUnavailable}
                                      className={cn(
                                        "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all",
                                        isSelected
                                          ? "border-primary bg-primary/10 text-white"
                                          : isUnavailable
                                            ? "border-red-200 bg-red-50/50 text-red-400 cursor-not-allowed opacity-60 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-500"
                                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                                      )}
                                    >
                                      <span className="text-sm font-medium">{localizeTime(slot.time, isRtl)}</span>
                                      <span className="text-xs text-muted-foreground">{localizeTime(displayEndTime, isRtl)}</span>
                                      {slot.isLateNight && (
                                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 mt-0.5">
                                          {t("booking.lateNight")}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Step 2: Services Cart — only after date AND time slot selected */}
                {selectedDate && form.watch("timeSlotId") && (
                  <div data-step="services">
                    <SectionHeader icon={ShoppingCart} title={t("booking.stepService")} step={2} />

                    <div className="space-y-4">
                      {/* First service: inline form; subsequent: button + dialog */}
                      {cartItems.length === 0 ? (
                        <div className="bg-red-50/50 dark:bg-red-950/10 rounded-xl p-4 border-2 border-red-300 dark:border-red-900/40 space-y-4">
                          <p className="text-sm font-medium text-red-600 dark:text-red-400">{t("booking.addService")}</p>

                          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-500">{t("booking.vehicleDisclaimerTitle")}</p>
                              <p className="text-xs text-yellow-700/80 dark:text-yellow-500/80 mt-0.5">{t("booking.vehicleDisclaimer")}</p>
                            </div>
                          </div>

                          {/* Vehicle Type Selection */}
                          <div className="grid grid-cols-3 gap-2">
                            {availableVehicleTypes.includes('sedan') && (
                              <Button type="button" variant={addVehicleType === 'sedan' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('sedan')} data-testid="button-add-sedan">
                                <Car className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.sedan")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('cuv') && (
                              <Button type="button" variant={addVehicleType === 'cuv' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('cuv')} data-testid="button-add-cuv">
                                <Car className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.cuv")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('suv') && (
                              <Button type="button" variant={addVehicleType === 'suv' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('suv')} data-testid="button-add-suv">
                                <Car className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.suv")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('van') && (
                              <Button type="button" variant={addVehicleType === 'van' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('van')} data-testid="button-add-van">
                                <Truck className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.van")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('motorcycle') && (
                              <Button type="button" variant={addVehicleType === 'motorcycle' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('motorcycle')} data-testid="button-add-motorcycle">
                                <Bike className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.motorcycle")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('caravan') && (
                              <Button type="button" variant={addVehicleType === 'caravan' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('caravan')} data-testid="button-add-caravan">
                                <Truck className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.caravan")}</span>
                              </Button>
                            )}
                            {availableVehicleTypes.includes('boat') && (
                              <Button type="button" variant={addVehicleType === 'boat' ? 'default' : 'outline'} className="h-auto min-h-[70px] py-2 flex-col gap-1" onClick={() => setAddVehicleType('boat')} data-testid="button-add-boat">
                                <Ship className="h-4 w-4" />
                                <span className="text-[10px] leading-tight">{t("booking.boat")}</span>
                              </Button>
                            )}
                          </div>

                          <MobileSelect
                            value=""
                            disabled={!addVehicleType}
                            onValueChange={handleServiceSelect}
                            placeholder={addVehicleType ? t("booking.selectService") : t("booking.selectVehicleType")}
                            className={cn("rounded-xl h-12", isRtl && "text-right")}
                            data-testid="select-add-service-inline"
                          >
                            {servicesLoading ? (
                              <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
                            ) : !services || services.length === 0 ? (
                              <MobileSelectItem value="no-services" disabled>No services available</MobileSelectItem>
                            ) : !addVehicleType ? (
                              <MobileSelectItem value="no-vehicle" disabled>{t("booking.selectVehicleType")}</MobileSelectItem>
                            ) : (
                              services.filter(s => !s.isAddOn).map((service) => {
                                if (!service.vehiclePricing || !service.vehiclePricing[addVehicleType] || service.vehiclePricing[addVehicleType] <= 0) {
                                  return null;
                                }
                                return (
                                  <MobileSelectItem key={service.id} value={service.id}>
                                    {isRtl ? service.nameAr : service.nameEn}
                                  </MobileSelectItem>
                                );
                              })
                            )}
                          </MobileSelect>
                        </div>
                      ) : null}

                      {/* Cart Items */}
                      {cartItems.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium flex items-center gap-2">
                            <ShoppingCart className="h-4 w-4" />
                            {t("booking.cartItems")} ({cartItems.length})
                          </p>
                          {cartItems.map((item, index) => {
                            // Get add-on names for display
                            const itemAddOns = item.addOns?.map(addOnId => {
                              const addOn = services?.find(s => s.id === addOnId);
                              if (!addOn) return null;
                              const name = isRtl ? addOn.nameAr : addOn.nameEn;
                              const quantity = getAddOnQuantity(item.addOnQuantities, addOnId);
                              return quantity > 1 ? `${name} ×${quantity}` : name;
                            }).filter(Boolean);

                            return (
                              <div
                                key={`${item.serviceId}-${item.vehicleType}-${index}`}
                                className="bg-primary/5 rounded-xl p-3 sm:p-3.5 border border-primary/20 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3"
                                data-testid={`cart-item-${index}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="font-semibold text-sm leading-snug">
                                      {isRtl ? item.serviceNameAr : item.serviceName}
                                    </p>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs shrink-0 mt-0.5 text-primary border-primary/30"
                                      onClick={() => setExpandedCartIndex(index)}
                                    >
                                      <Sparkles className="h-3 w-3 fill-primary" />
                                      {t("booking.seeDetails")}
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {t(`booking.${item.vehicleType}`)}
                                    {item.isPerUnitPricing && item.unit && (
                                      <> - {item.unit} {t("booking.feet")}</>
                                    )}
                                    {item.duration && (
                                      <> ({item.duration} {t("services.minsEstimated")})</>
                                    )}
                                  </p>
                                  {/* Add-ons as chips + an inline control, so the row
                                      matches the rest of the card instead of a bare
                                      comma list and an underlined link. Per-unit lines
                                      fold add-ons into unitPrice and use their own
                                      dialog, so they only get the read-only chips. */}
                                  {((itemAddOns && itemAddOns.length > 0) ||
                                    (!item.isPerUnitPricing && getItemAddOnsFor(item.serviceId).length > 0)) && (
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                      {itemAddOns?.map((name) => (
                                        <span
                                          key={String(name)}
                                          className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-medium"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                      {!item.isPerUnitPricing &&
                                        getItemAddOnsFor(item.serviceId).length > 0 && (
                                          <button
                                            type="button"
                                            onClick={() => editCartItemAddOns(index)}
                                            className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/40 px-2 py-0.5 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10 hover:border-primary/60"
                                            data-testid={`button-edit-addons-${index}`}
                                          >
                                            {itemAddOns && itemAddOns.length > 0 ? (
                                              <Sparkles className="h-3 w-3" />
                                            ) : (
                                              <Plus className="h-3 w-3" />
                                            )}
                                            {itemAddOns && itemAddOns.length > 0
                                              ? t("booking.editAddOns")
                                              : t("booking.addAddOns")}
                                          </button>
                                        )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 pt-2.5 sm:pt-0 border-t border-primary/15 sm:border-t-0">
                                  {/* Only show quantity controls for non-per-unit services */}
                                  {!item.isPerUnitPricing && (
                                    <div className="flex items-center gap-1 bg-background rounded-lg border border-border px-1">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => updateCartItemQuantity(index, -1)}
                                        data-testid={`button-cart-decrease-${index}`}
                                      >
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => addAnotherCar(index)}
                                        data-testid={`button-cart-increase-${index}`}
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                  <div className="ms-auto flex items-center gap-2">
                                    <span className="text-sm font-bold text-white min-w-[60px] text-end">
                                      {item.subtotal.toFixed(3)} {t("services.currency")}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:text-destructive"
                                      onClick={() => removeFromCart(index)}
                                      data-testid={`button-remove-cart-${index}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setAddVehicleType(null);
                              setShowAddServiceDialog(true);
                            }}
                            className="w-full h-14 rounded-xl border-dashed text-base"
                            data-testid="button-open-add-service"
                          >
                            <Plus className="h-5 w-5 me-2" />
                            {t("booking.addAnotherService")}
                          </Button>

                          {/* Order-wide global add-ons (scope 'order') — shown after
                              the add-service button so the customer can attach them
                              to the whole order. */}
                          {globalOrderAddOns.length > 0 && (
                            <div className="pt-2 mt-2 border-t border-border/50">
                              <p className="text-sm font-medium mb-2">{t("booking.addOns")}</p>
                              <div className="space-y-1.5">
                                {globalOrderAddOns.map((addOn) => {
                                  const checked = orderAddOns.includes(addOn.id);
                                  const quantity = getAddOnQuantity(orderAddOnQty, addOn.id);
                                  return (
                                    // The row is a button, so the stepper sits
                                    // beside it — nested buttons are invalid.
                                    <div
                                      key={addOn.id}
                                      className={cn(
                                        "w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all",
                                        checked
                                          ? "border-primary bg-primary/5"
                                          : "border-border hover:border-primary/50",
                                      )}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOrderAddOns((prev) =>
                                            prev.includes(addOn.id)
                                              ? prev.filter((id) => id !== addOn.id)
                                              : [...prev, addOn.id],
                                          );
                                          setOrderAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                                        }}
                                        className="flex-1 min-w-0 flex items-center gap-3 text-start focus:outline-none"
                                        data-testid={`order-addon-${addOn.id}`}
                                        aria-pressed={checked}
                                      >
                                        <span
                                          className={cn(
                                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                            checked
                                              ? "border-primary bg-primary text-primary-foreground"
                                              : "border-muted-foreground/40",
                                          )}
                                        >
                                          {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                          <span className="block text-sm font-medium truncate">
                                            {isRtl ? addOn.nameAr : addOn.nameEn}
                                          </span>
                                          {(isRtl ? addOn.descriptionAr : addOn.descriptionEn) && (
                                            <span className="text-xs text-muted-foreground line-clamp-1">
                                              {isRtl ? addOn.descriptionAr : addOn.descriptionEn}
                                            </span>
                                          )}
                                        </span>
                                        <span
                                          className={cn(
                                            "text-sm font-medium shrink-0 transition-colors",
                                            checked ? "text-primary" : "text-muted-foreground",
                                          )}
                                        >
                                          +{(addOn.price * (checked ? quantity : 1)).toFixed(3)} {t("services.currency")}
                                        </span>
                                      </button>
                                      {addOn.allowQuantity && (
                                        <AddOnQuantityStepper
                                          quantity={checked ? quantity : 0}
                                          onChange={(delta) =>
                                            setOrderAddOnQty((prev) => stepAddOnQuantity(prev, addOn.id, delta))
                                          }
                                          onSelect={() => {
                                            setOrderAddOns((prev) =>
                                              prev.includes(addOn.id) ? prev : [...prev, addOn.id],
                                            );
                                            setOrderAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                                          }}
                                          onDeselect={() => {
                                            setOrderAddOns((prev) => prev.filter((id) => id !== addOn.id));
                                            setOrderAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                                          }}
                                          testId={`order-addon-qty-${addOn.id}`}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div className="flex justify-between items-center pt-2 border-t border-border/50 mt-2">
                            <span className="text-sm text-muted-foreground">{t("booking.cartSubtotal")}</span>
                            <span className="font-semibold text-primary">{cartSubtotal.toFixed(3)} {t("services.currency")}</span>
                          </div>
                          {orderAddOnsTotal > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted-foreground">{t("booking.addOns")}</span>
                              <span className="font-semibold text-primary">+{orderAddOnsTotal.toFixed(3)} {t("services.currency")}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">{t("booking.totalDuration")}</span>
                            <span className="font-medium">{formatDuration(cartItems.reduce((total, item) => total + (item.duration || 0) * item.quantity, 0))}</span>
                          </div>
                        </div>
                      )}

                      {cartItems.length === 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t("booking.emptyCart")}</p>
                        </div>
                      )}

                      {cartItems.length === 0 && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
                          <p className="text-sm text-red-600 dark:text-red-400 font-medium">{t("booking.validation.serviceRequired")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Time Slot Warning Alert */}
                {showTimeSlotWarning && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="space-y-3">
                      <div>
                        {hasAvailableSlots ? `${t("booking.timeSlotNoLongerAvailableDesc")} (${formatDuration(cartItems.reduce((total, item) => total + (item.duration || 0) * item.quantity, 0))})` : t("booking.timeSlotNoLongerAvailableNoSlots")}
                      </div>
                      {/* Show which services the selected slot supports */}
                      {(() => {
                        const selectedSlot = timeSlots?.find(s => s.id === form.watch("timeSlotId"));
                        const slotServiceIds = (selectedSlot as any)?.serviceIds || [];
                        if (slotServiceIds.length === 0) return null;
                        const supportedNames = slotServiceIds
                          .map((id: string) => services?.find(s => s.id === id))
                          .filter(Boolean)
                          .map((s: any) => isRtl ? s.nameAr : s.nameEn);
                        if (supportedNames.length === 0) return null;
                        return (
                          <div className="text-xs">
                            <span className="font-medium">{t("booking.slotSupports")}:</span>{" "}
                            <span>{supportedNames.join(", ")}</span>
                          </div>
                        );
                      })()}
                      <Button
                        type="button"
                        variant="outline"
                        className="bg-background border-destructive/50 text-destructive hover:bg-destructive/10"
                        onClick={() => dateTimeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      >
                        {t("booking.selectDifferentTime")}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                {/* Step 3: Location — only after services added to cart */}
                {selectedDate && form.watch("timeSlotId") && cartItems.length > 0 && (
                  <div className="border-t border-border/50 pt-8" data-step="location">
                    <SectionHeader icon={MapPin} title={t("booking.stepLocation")} step={3} />

                    <div className="space-y-4">
                      {savedAddresses && savedAddresses.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              type="button"
                              variant={!useManualAddress ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                setUseManualAddress(false);
                                if (selectedAddressId && savedAddresses?.find(a => a.id === selectedAddressId)) {
                                  handleAddressSelect(selectedAddressId);
                                } else if (savedAddresses && savedAddresses.length > 0) {
                                  const defaultAddr = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
                                  if (defaultAddr) handleAddressSelect(defaultAddr.id);
                                }
                              }}
                              className="rounded-full"
                              data-testid="button-use-saved"
                            >
                              {t("addresses.useSaved")}
                            </Button>
                            <Button
                              type="button"
                              variant={useManualAddress ? "default" : "outline"}
                              size="sm"
                              onClick={handleManualAddressToggle}
                              className="rounded-full"
                              data-testid="button-enter-manually"
                            >
                              {t("addresses.enterManually")}
                            </Button>
                          </div>

                          {!useManualAddress && (
                            <>
                              <MobileSelect
                                value={selectedAddressId || undefined}
                                onValueChange={(value) => {
                                  if (value) {
                                    handleAddressSelect(value);
                                  }
                                }}
                                placeholder={t("addresses.selectAddress")}
                                className="rounded-xl h-12"
                                data-testid="select-saved-address"
                              >
                                {savedAddresses?.map((addr) => (
                                  <MobileSelectItem key={addr.id} value={addr.id}>
                                    <span className="flex items-center gap-2">
                                      {addr.label}
                                      {addr.isDefault && (
                                        <span className="text-xs text-primary">({t("addresses.default")})</span>
                                      )}
                                    </span>
                                  </MobileSelectItem>
                                ))}
                              </MobileSelect>

                              {selectedAddressId && (() => {
                                const addr = savedAddresses.find(a => a.id === selectedAddressId);
                                if (!addr) return null;
                                const gov = addr.governorate || governorates?.find(g => g.id === addr.governorateId);
                                const dist = addr.district || districts?.find(d => d.id === addr.districtId);
                                const govName = isRtl ? gov?.nameAr : gov?.nameEn;
                                const distName = isRtl ? dist?.nameAr : dist?.nameEn;
                                return (
                                  <div className="bg-muted/50 rounded-lg p-3 text-sm" data-testid="saved-address-summary">
                                    <p className="font-medium">{addr.label}</p>
                                    {(govName || distName) && (
                                      <p className="text-muted-foreground">
                                        {[govName, distName].filter(Boolean).join(", ")}
                                      </p>
                                    )}
                                    <p className="text-muted-foreground">
                                      {[addr.block && `${t("addresses.block")} ${addr.block}`, addr.street && `${t("addresses.street")} ${addr.street}`, addr.avenue && `${t("addresses.avenue")} ${addr.avenue}`, addr.building && `${t("addresses.building")} ${addr.building}`].filter(Boolean).join(", ")}
                                    </p>
                                    {addr.landmark && <p className="text-muted-foreground text-xs">{t("addresses.landmark")}: {addr.landmark}</p>}
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      )}

                      {(!selectedAddressId || useManualAddress) && (
                        <div className={cn("grid md:grid-cols-2 gap-4", isRtl && "md:grid-flow-dense")}>
                          <FormField
                            control={form.control}
                            name="governorateId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm">{t("booking.governorate")}</FormLabel>
                                <FormControl>
                                  <MobileSelect
                                    onValueChange={field.onChange}
                                    value={field.value}
                                    placeholder={t("booking.selectGovernorate")}
                                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                                    data-testid="select-governorate"
                                  >
                                    {governoratesLoading ? (
                                      <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
                                    ) : !governorates || governorates.length === 0 ? (
                                      <MobileSelectItem value="no-governorates" disabled>{t("booking.noGovernorates")}</MobileSelectItem>
                                    ) : (
                                      governorates.map((gov) => (
                                        <MobileSelectItem key={gov.id} value={gov.id}>
                                          {isRtl ? gov.nameAr : gov.nameEn}
                                        </MobileSelectItem>
                                      ))
                                    )}
                                  </MobileSelect>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="districtId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm">{t("booking.district")}</FormLabel>
                                <FormControl>
                                  <MobileSelect
                                    onValueChange={field.onChange}
                                    value={field.value}
                                    disabled={!selectedGovernorateId}
                                    placeholder={selectedGovernorateId ? t("booking.selectDistrict") : t("booking.selectGovernorateFirst")}
                                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                                    data-testid="select-district"
                                  >
                                    {districtsLoading ? (
                                      <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
                                    ) : !districts || districts.length === 0 ? (
                                      <MobileSelectItem value="no-districts" disabled>{t("booking.noDistricts")}</MobileSelectItem>
                                    ) : (
                                      districts.map((district) => (
                                        <MobileSelectItem key={district.id} value={district.id}>
                                          {isRtl ? district.nameAr : district.nameEn}
                                        </MobileSelectItem>
                                      ))
                                    )}
                                  </MobileSelect>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}

                      {deliveryFee > 0 && (
                        <div className="flex items-center justify-between text-sm bg-muted/50 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {t("booking.deliveryFee")}
                          </span>
                          <span className="font-medium">{deliveryFee.toFixed(3)} {t("services.currency")}</span>
                        </div>
                      )}

                      {(useManualAddress || (!savedAddresses || savedAddresses.length === 0)) && (
                        <div className="space-y-4 border-t border-border/50 pt-4">
                          <div className={cn("grid md:grid-cols-4 gap-4", isRtl && "md:grid-flow-dense")}>
                            <FormField
                              control={form.control}
                              name="block"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm">{t("addresses.block")} *</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("addresses.blockPlaceholder")}
                                      {...field}
                                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                                      dir={isRtl ? "rtl" : "ltr"}
                                      data-testid="input-block"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="street"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm">{t("addresses.street")} *</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("addresses.streetPlaceholder")}
                                      {...field}
                                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                                      dir={isRtl ? "rtl" : "ltr"}
                                      data-testid="input-street"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="avenue"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm">{t("addresses.avenue")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("addresses.avenuePlaceholder")}
                                      {...field}
                                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                                      dir={isRtl ? "rtl" : "ltr"}
                                      data-testid="input-avenue"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="building"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm">{t("addresses.building")} *</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("addresses.buildingPlaceholder")}
                                      {...field}
                                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                                      dir={isRtl ? "rtl" : "ltr"}
                                      data-testid="input-building"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name="landmark"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm">{t("addresses.landmark")}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t("addresses.landmarkPlaceholder")}
                                    {...field}
                                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                                    dir={isRtl ? "rtl" : "ltr"}
                                    data-testid="input-landmark"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="saveAddress"
                              checked={form.watch("saveAddress")}
                              onCheckedChange={(checked) => form.setValue("saveAddress", !!checked)}
                              data-testid="checkbox-save-address"
                            />
                            <label htmlFor="saveAddress" className="text-sm cursor-pointer">
                              {t("addresses.saveForFuture")}
                            </label>
                          </div>

                          {form.watch("saveAddress") && (
                            <>
                            <FormField
                              control={form.control}
                              name="addressLabel"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm">{t("addresses.labelOptional")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("addresses.labelPlaceholder")}
                                      {...field}
                                      className={cn("rounded-xl h-12", isRtl && "text-right")}
                                      dir={isRtl ? "rtl" : "ltr"}
                                      data-testid="input-address-label"
                                    />
                                  </FormControl>
                                  <FormDescription className="text-xs">
                                    {t("addresses.labelDescription")}
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Map location — only when saving address */}
                            <MapPicker
                              latitude={bookingLat}
                              longitude={bookingLng}
                              onChange={(lat, lng) => { setBookingLat(lat); setBookingLng(lng); }}
                            />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Order Summary */}
                {(cartItems.length > 0 && selectedGovernorateId && selectedDistrictId && addressReady && selectedDate && form.watch("timeSlotId") && !showTimeSlotWarning) && (
                  <div className="border-t border-border/50 pt-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg">{t("booking.orderSummary")}</h3>
                    </div>

                    <div className="bg-muted/30 rounded-xl p-4 space-y-3 border border-border/50">
                      {/* Cart Items Summary */}
                      <div className="space-y-2">
                        {cartItems.map((item, index) => {
                          // Get add-on names for display
                          const itemAddOns = item.addOns?.map(addOnId => {
                            const addOn = services?.find(s => s.id === addOnId);
                            if (!addOn) return null;
                            const name = isRtl ? addOn.nameAr : addOn.nameEn;
                            const quantity = getAddOnQuantity(item.addOnQuantities, addOnId);
                            return quantity > 1 ? `${name} ×${quantity}` : name;
                          }).filter(Boolean);

                          return (
                            <div key={index} className="flex justify-between items-center text-sm">
                              <span className="text-muted-foreground">
                                {isRtl ? item.serviceNameAr : item.serviceName} ({t(`booking.${item.vehicleType}`)}) × {item.quantity}
                                {itemAddOns && itemAddOns.length > 0 && (
                                  <span className="text-xs text-primary block">+ {itemAddOns.join(', ')}</span>
                                )}
                              </span>
                              <span className="font-medium">{item.subtotal.toFixed(3)} {t("services.currency")}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Shop products delivered with this wash — browse the shop in a
                          drawer (BookingShopSheet) so the booking stays filled in. */}
                      <div className="border-t border-dashed border-border/60 pt-3 space-y-2">
                        {shopCart.items.length === 0 ? (
                          <button
                            type="button"
                            onClick={() => setShopSheetOpen(true)}
                            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed border-primary/40 hover-elevate"
                            data-testid="button-open-booking-shop"
                          >
                            <div className="flex items-center gap-3">
                              <ShoppingCart className="w-5 h-5 text-primary" />
                              <div className="text-start">
                                <p className="text-sm font-medium">{t("shop.attach.browse")}</p>
                                <p className="text-xs text-muted-foreground">{t("shop.attach.browseHint")}</p>
                              </div>
                            </div>
                            <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                          </button>
                        ) : (
                        <>
                          <div
                            role="checkbox"
                            aria-checked={includeShopCart}
                            tabIndex={0}
                            onClick={() => setIncludeShopCart(!includeShopCart)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setIncludeShopCart(!includeShopCart);
                              }
                            }}
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
                              includeShopCart
                                ? "border-primary bg-primary/5"
                                : "border-border hover-elevate"
                            )}
                            data-testid="button-include-shop-cart"
                          >
                            <div className="flex items-center gap-3">
                              <ShoppingCart className={cn("w-5 h-5", includeShopCart ? "text-primary" : "text-muted-foreground")} />
                              <div className={cn("text-start", isRtl && "text-end")}>
                                <p className={cn("text-sm font-medium", includeShopCart && "text-primary")}>
                                  {t("shop.attach.title")}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t("shop.attach.subtitle", {
                                    count: shopCart.count,
                                    total: `${shopCart.subtotal.toFixed(3)} ${t("services.currency")}`,
                                  })}
                                </p>
                              </div>
                            </div>
                            <div className={cn(
                              "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0",
                              includeShopCart
                                ? "bg-primary border-primary"
                                : "border-muted-foreground/50"
                            )} data-testid="checkbox-include-shop-cart">
                              {includeShopCart && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                            </div>
                          </div>

                          {includeShopCart && (
                            <div className="space-y-1.5 px-1">
                              {shopCart.items.map((item) => (
                                <div key={item.productId} className="flex justify-between items-center text-sm">
                                  <span className="text-muted-foreground flex items-center gap-1.5">
                                    <Package className="w-3.5 h-3.5 text-primary/70 flex-shrink-0" />
                                    {isRtl ? item.nameAr : item.nameEn} × {item.quantity}
                                  </span>
                                  <span className="font-medium">{(item.price * item.quantity).toFixed(3)} {t("services.currency")}</span>
                                </div>
                              ))}
                              <p className="text-xs text-muted-foreground">{t("shop.attach.note")}</p>
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={() => setShopSheetOpen(true)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            data-testid="button-add-more-products"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            {t("shop.attach.addMore")}
                          </button>
                        </>
                        )}
                      </div>

                      <div className="flex justify-between items-start text-sm gap-3">
                        <span className="text-muted-foreground flex-shrink-0">{t("booking.location")}</span>
                        <span className="font-medium text-right">
                          {(() => {
                            const gov = governorates?.find(g => g.id === selectedGovernorateId);
                            const dist = districts?.find(d => d.id === selectedDistrictId);
                            const areaName = `${isRtl ? gov?.nameAr : gov?.nameEn}, ${isRtl ? dist?.nameAr : dist?.nameEn}`;
                            const customerAddress = form.watch("customerAddress");
                            return customerAddress ? `${areaName} - ${customerAddress}` : areaName;
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">{t("booking.dateTime")}</span>
                        <span className="font-medium">
                          {format(selectedDate, "PPP", { locale: isRtl ? ar : enUS })} - {(() => {
                            const slot = timeSlots?.find(s => s.id === form.watch("timeSlotId"));
                            if (!slot) return "";
                            const start = localizeTime(slot.time, isRtl);
                            const end = localizeTime(computeEndTime(slot.time), isRtl);
                            return `${start} - ${end}`;
                          })()}
                        </span>
                      </div>

                      <div className="border-t border-border/50 pt-3 mt-3 space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">{t("booking.cartSubtotal")}</span>
                          <span>{cartSubtotal.toFixed(3)} {t("services.currency")}</span>
                        </div>
                        {orderAddOnsTotal > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">{t("booking.addOns")}</span>
                            <span>{orderAddOnsTotal.toFixed(3)} {t("services.currency")}</span>
                          </div>
                        )}
                        {deliveryFee > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">{t("booking.deliveryFee")}</span>
                            <span>{deliveryFee.toFixed(3)} {t("services.currency")}</span>
                          </div>
                        )}
                        {shopProductsTotal > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">{t("shop.attach.totalLine")}</span>
                            <span>{shopProductsTotal.toFixed(3)} {t("services.currency")}</span>
                          </div>
                        )}

                        {/* Coupon Section */}
                        <div className="border-t border-border/50 pt-3 mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Tag className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{t("booking.coupon.title")}</span>
                          </div>

                          {!couponApplied ? (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  placeholder={t("booking.coupon.placeholder")}
                                  value={couponInput}
                                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                  className={cn("rounded-lg h-10 flex-1", isRtl && "text-right")}
                                  dir={isRtl ? "rtl" : "ltr"}
                                  data-testid="input-coupon"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={validateCoupon}
                                  disabled={!couponInput.trim() || couponValidating}
                                  className="h-10"
                                  data-testid="button-apply-coupon"
                                >
                                  {couponValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : t("booking.coupon.apply")}
                                </Button>
                              </div>
                              {couponError && (
                                <p className="text-xs text-destructive">{couponError}</p>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-green-700 dark:text-green-300">{couponCode}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={removeCoupon}
                                className="h-8 w-8 text-muted-foreground"
                                data-testid="button-remove-coupon"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {couponApplied && couponDiscount > 0 && (
                          <div className="flex justify-between items-center text-sm text-green-600 dark:text-green-400">
                            <span>{t("booking.coupon.discount")}</span>
                            <span>-{couponDiscount.toFixed(3)} {t("services.currency")}</span>
                          </div>
                        )}

                        {/* Wallet Credit Section */}
                        {walletBalance > 0 && (
                          <div className="border-t border-border/50 pt-3 mt-3 space-y-3">
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setUseWalletCredit(!useWalletCredit);
                                if (useWalletCredit) setCustomWalletAmount("");
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setUseWalletCredit(!useWalletCredit);
                                  if (useWalletCredit) setCustomWalletAmount("");
                                }
                              }}
                              className={cn(
                                "w-full flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
                                useWalletCredit
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover-elevate"
                              )}
                              data-testid="button-use-wallet-credit"
                            >
                              <div className="flex items-center gap-3">
                                <Wallet className={cn("w-5 h-5", useWalletCredit ? "text-primary" : "text-muted-foreground")} />
                                <div className={cn("text-start", isRtl && "text-end")}>
                                  <p className={cn("text-sm font-medium", useWalletCredit && "text-primary")}>
                                    {t("booking.payment.useWalletCredit")}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {t("booking.payment.walletBalance")}: {walletBalance.toFixed(3)} {t("services.currency")}
                                  </p>
                                </div>
                              </div>
                              <div className={cn(
                                "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                                useWalletCredit
                                  ? "bg-primary border-primary"
                                  : "border-muted-foreground/50"
                              )} data-testid="checkbox-wallet-credit">
                                {useWalletCredit && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                              </div>
                            </div>

                            {useWalletCredit && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max={maxWalletCredit}
                                    placeholder={`${t("booking.payment.customAmount")} (${t("booking.payment.max")}: ${maxWalletCredit.toFixed(3)})`}
                                    value={customWalletAmount}
                                    onChange={(e) => setCustomWalletAmount(e.target.value)}
                                    className="flex-1"
                                    data-testid="input-wallet-amount"
                                  />
                                  <span className="text-sm text-muted-foreground whitespace-nowrap">{t("services.currency")}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {t("booking.payment.leaveEmptyForMax")}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex justify-between items-center pt-2 border-t border-border/50">
                          <span className="font-semibold">{t("booking.totalPrice")}</span>
                          <span className="text-xl font-bold text-white">{totalPrice.toFixed(3)} {t("services.currency")}</span>
                        </div>

                        {useWalletCredit && walletCreditUsed > 0 && (
                          <>
                            <div className="flex justify-between items-center text-sm text-green-600 dark:text-green-400">
                              <span>{t("booking.payment.walletCreditApplied")}</span>
                              <span>-{walletCreditUsed.toFixed(3)} {t("services.currency")}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-medium">
                              <span>{t("booking.payment.cashAmountDue")}</span>
                              <span className="text-primary">{cashAmountDue.toFixed(3)} {t("services.currency")}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Notes */}
                {cartItems.length > 0 && selectedGovernorateId && selectedDistrictId && addressReady && selectedDate && form.watch("timeSlotId") && !showTimeSlotWarning && (
                  <div className="border-t border-border/50 pt-8">
                    <SectionHeader icon={Car} title={t("booking.stepDetails")} step={4} />

                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">{t("booking.notes")}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("booking.notesPlaceholder")}
                                {...field}
                                className={cn("rounded-xl h-12", isRtl && "text-right")}
                                dir={isRtl ? "rtl" : "ltr"}
                                data-testid="input-notes"
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              {t("booking.notesDescription")}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Payment — no gateway choice here. Wallet-covered orders
                    are confirmed directly; anything else goes to the UPayments page
                    (KNET / card / Apple Pay are chosen there) on Confirm Booking. */}
                {(cartItems.length > 0 && selectedGovernorateId && selectedDistrictId && addressReady && selectedDate && form.watch("timeSlotId") && !showTimeSlotWarning) && (
                  <div className="border-t border-border/50 pt-8">
                    {cashAmountDue === 0 && useWalletCredit ? (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-4 h-4 text-primary" />
                          </div>
                          <h3 className="font-semibold text-lg">{t("booking.selectPaymentMethod")}</h3>
                        </div>
                        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                          <Wallet className="w-6 h-6 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-medium text-green-700 dark:text-green-300">
                              {t("booking.payment.fullyPaidByWallet")}
                            </p>
                            <p className="text-sm text-green-600 dark:text-green-400">
                              {t("booking.payment.noAdditionalPayment")}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-start gap-3 text-sm text-muted-foreground" data-testid="payment-online-note">
                        <CreditCard className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                        <div>
                          <p>{t("booking.payment.redirectNote", { amount: cashAmountDue.toFixed(3) })}</p>
                          <p className="text-xs mt-1">{t("booking.payment.secureNote")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Continue/Confirm button — always visible; validates step-by-step on click */}
                <Button
                  type="submit"
                  className="w-full h-14 text-lg font-semibold rounded-xl sticky bottom-4 z-10 shadow-lg"
                  disabled={isPending}
                  data-testid="button-submit-booking"
                >
                    {isPending ? (
                      <>
                        <Loader2 className="me-2 h-5 w-5 animate-spin" />
                        {t("booking.processing")}
                      </>
                    ) : (
                      t("booking.confirmBooking")
                    )}
                  </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* Vehicle Type Selection Dialog */}
      <BookingShopSheet
        open={shopSheetOpen}
        onOpenChange={(open) => {
          setShopSheetOpen(open);
          // Products picked from inside the booking are meant for this wash.
          if (!open && shopCart.items.length > 0) setIncludeShopCart(true);
        }}
      />

      <Dialog open={showVehicleDialog} onOpenChange={(open) => {
        if (!open) {
          setPendingService(null);
        }
        setShowVehicleDialog(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("booking.selectVehicleType")}</DialogTitle>
            <DialogDescription>
              {pendingService && (i18n.language === 'ar' ? pendingService.nameAr : pendingService.nameEn)}
            </DialogDescription>
          </DialogHeader>
          {pendingService?.vehiclePricing && (
            <div className="grid grid-cols-3 gap-4 py-4">
              {Object.entries(pendingService.vehiclePricing)
                .filter(([_, price]) => price !== null && price !== undefined && Number(price) > 0)
                .sort(([a], [b]) => {
                  const order: Record<string, number> = { sedan: 1, cuv: 2, suv: 3, van: 4, motorcycle: 5, caravan: 6, boat: 7 };
                  return (order[a] || 99) - (order[b] || 99);
                })
                .map(([vehicleType, price]) => {
                  const vehicleIcons: Record<string, React.ReactNode> = {
                    sedan: <Car className="h-8 w-8" />,
                    suv: <Car className="h-8 w-8" />,
                    cuv: <Car className="h-8 w-8" />,
                    van: <Truck className="h-8 w-8" />,
                    motorcycle: <Bike className="h-8 w-8" />,
                    caravan: <Truck className="h-8 w-8" />,
                    boat: <Ship className="h-8 w-8" />,
                  };
                  return (
                    <Button
                      key={vehicleType}
                      variant="outline"
                      className="h-24 flex flex-col items-center justify-center gap-2"
                      onClick={() => handleVehicleSelect(vehicleType as any)}
                      data-testid={`button-select-${vehicleType}`}
                    >
                      {vehicleIcons[vehicleType] || <Car className="h-8 w-8" />}
                      <span className="font-medium">{t(`booking.${vehicleType}`)}</span>
                      <span className="text-sm text-muted-foreground">
                        {Number(price).toFixed(3)} {t("services.currency")}
                      </span>
                    </Button>
                  );
                })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-Unit Pricing Dialog (Caravan/Boat) */}
      <Dialog open={showUnitDialog} onOpenChange={(open) => {
        if (!open) {
          setPendingUnitService(null);
          setSelectedAddOns([]);
        }
        setShowUnitDialog(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingUnitService && (i18n.language === 'ar' ? pendingUnitService.nameAr : pendingUnitService.nameEn)}
            </DialogTitle>
            <DialogDescription>
              {t("booking.selectFeetDescription")}
            </DialogDescription>
          </DialogHeader>

          {pendingUnitService && (
            <div className="space-y-6 py-4">
              {/* Feet Slider */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t("booking.lengthInFeet")}</span>
                  <span className="text-lg font-bold text-primary">{selectedFeet} {t("booking.feet")}</span>
                </div>
                <Slider
                  value={[selectedFeet]}
                  onValueChange={(value) => setSelectedFeet(value[0])}
                  min={pendingUnitService.minUnit || 10}
                  max={pendingUnitService.maxUnit || 50}
                  step={1}
                  className="w-full"
                  data-testid="slider-feet"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{pendingUnitService.minUnit || 10} {t("booking.feet")}</span>
                  <span>{pendingUnitService.maxUnit || 50} {t("booking.feet")}</span>
                </div>
              </div>

              {/* Price & Duration Display */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">{t("booking.basePrice")}</span>
                  <span className="font-medium">
                    {calculatePerUnitPrice(pendingUnitService, selectedFeet).toFixed(3)} {t("services.currency")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">{t("booking.estimatedDuration")}</span>
                  <span className="font-medium">
                    {calculatePerUnitDuration(pendingUnitService, selectedFeet)} {t("services.minsEstimated")}
                  </span>
                </div>
              </div>

              {/* Add-ons (if available) */}
              {availableAddOnServices.length > 0 && (
                <div className="space-y-3">
                  <span className="text-sm font-medium">{t("booking.optionalAddOns")}</span>
                  {availableAddOnServices.map((addOn) => {
                    const checked = selectedAddOns.includes(addOn.id);
                    const quantity = getAddOnQuantity(selectedAddOnQty, addOn.id);
                    return (
                      <div
                        key={addOn.id}
                        className={cn(
                          "flex items-center justify-between gap-2 p-3 rounded-lg border cursor-pointer transition-colors",
                          checked
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        )}
                        onClick={() => toggleAddOn(addOn.id)}
                        data-testid={`addon-${addOn.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleAddOn(addOn.id)}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              {i18n.language === 'ar' ? addOn.nameAr : addOn.nameEn}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              +{addOn.duration} {t("services.minsEstimated")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {addOn.allowQuantity && (
                            <AddOnQuantityStepper
                              quantity={checked ? quantity : 0}
                              onChange={(delta) =>
                                setSelectedAddOnQty((prev) => stepAddOnQuantity(prev, addOn.id, delta))
                              }
                              onSelect={() => toggleAddOn(addOn.id)}
                              onDeselect={() => toggleAddOn(addOn.id)}
                              testId={`addon-qty-${addOn.id}`}
                            />
                          )}
                          <span className="text-sm font-medium text-primary">
                            +{(getAddOnPrice(addOn, pendingUnitVehicleType) * (checked ? quantity : 1)).toFixed(3)} {t("services.currency")}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Total */}
              {(selectedAddOns.length > 0 || addOnsTotalPrice > 0) && (
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t("booking.total")}</span>
                    <span className="text-primary">
                      {(calculatePerUnitPrice(pendingUnitService, selectedFeet) + addOnsTotalPrice).toFixed(3)} {t("services.currency")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("booking.totalDuration")}: {formatDuration(calculatePerUnitDuration(pendingUnitService, selectedFeet) + addOnsTotalDuration)}
                  </p>
                </div>
              )}

              {/* Add to Cart Button */}
              <Button
                className="w-full"
                onClick={addPerUnitServiceToCart}
                data-testid="button-add-unit-service"
              >
                {t("booking.addToCart")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Detail Dialog for Cart Items */}
      <Dialog open={expandedCartIndex !== null} onOpenChange={(open) => { if (!open) setExpandedCartIndex(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0 rounded-2xl border-0 shadow-2xl [&>[data-radix-dialog-close]]:hidden">
          {expandedCartIndex !== null && (() => {
            const item = cartItems[expandedCartIndex];
            const service = services?.find(s => s.id === item.serviceId);
            if (!service) return null;
            const descRaw = isRtl ? service.descriptionAr : service.descriptionEn;
            const descItems = descRaw.split(/[،,]/).map((s: string) => s.trim()).filter((s: string) => s.length > 0);
            return (
              <>
                <button
                  onClick={() => setExpandedCartIndex(null)}
                  className="absolute right-4 top-4 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white p-2 backdrop-blur-sm transition-all"
                >
                  <X className="h-4 w-4" />
                </button>
                {service.imageUrl && (
                  <div className="relative h-44 w-full">
                    <img src={service.imageUrl} alt={isRtl ? service.nameAr : service.nameEn} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
                  </div>
                )}
                <div className={`px-6 pb-6 ${service.imageUrl ? '-mt-6 relative z-10' : 'pt-6'}`}>
                  <DialogHeader className="mb-3 text-start">
                    <DialogTitle className="text-xl font-bold font-display leading-tight text-start">
                      {isRtl ? item.serviceNameAr : item.serviceName}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="flex flex-wrap items-center gap-3 mb-5 pb-5 border-b border-border/50">
                    <span className="text-2xl font-bold text-white">{item.unitPrice.toFixed(3)} {t("services.currency")}</span>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground ms-auto bg-muted/50 px-3 py-1 rounded-full">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{t("services.durationPrefix")} {service.duration} {t("services.minsEstimated")}</span>
                    </div>
                  </div>

                  {descItems.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-bold uppercase tracking-wide">{t("services.whatsIncluded")}</span>
                      </div>
                      <ul className="space-y-2.5">
                        {descItems.map((descItem: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3 h-3 text-primary" />
                            </div>
                            <span className="text-sm text-muted-foreground leading-relaxed">{descItem}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Prompt old customers (no preferences) to set them once. */}
      <SetPreferencesDialog
        open={showPreferencesDialog}
        onSaved={() => setPreferencesDismissed(true)}
        onDismiss={() => setPreferencesDismissed(true)}
      />

      {/* Blocked/Deactivated Account Dialog */}
      <AlertDialog open={showBlockedDialog} onOpenChange={setShowBlockedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("booking.toast.accountDeactivated.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("booking.toast.accountDeactivated.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowBlockedDialog(false)}>{t("booking.ok")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Service Dialog */}
      <Dialog open={showAddServiceDialog} onOpenChange={setShowAddServiceDialog}>
        {/* Hides the default DialogContent close button — the footer's إلغاء is the way out. */}
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              {cartItems.length > 0 ? t("booking.addAnotherService") : t("booking.addService")}
            </DialogTitle>
            <DialogDescription>{t("booking.selectService")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-500">{t("booking.vehicleDisclaimerTitle")}</p>
                <p className="text-xs text-yellow-700/80 dark:text-yellow-500/80 mt-0.5">{t("booking.vehicleDisclaimer")}</p>
              </div>
            </div>

            {/* Vehicle Type Selection */}
            <div className="grid grid-cols-3 gap-2">
              {availableVehicleTypes.includes('sedan') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'sedan' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('sedan')}
                  data-testid="button-add-sedan"
                >
                  <Car className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.sedan")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('cuv') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'cuv' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('cuv')}
                  data-testid="button-add-cuv"
                >
                  <Car className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.cuv")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('suv') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'suv' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('suv')}
                  data-testid="button-add-suv"
                >
                  <Car className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.suv")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('van') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'van' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('van')}
                  data-testid="button-add-van"
                >
                  <Truck className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.van")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('motorcycle') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'motorcycle' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('motorcycle')}
                  data-testid="button-add-motorcycle"
                >
                  <Bike className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.motorcycle")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('caravan') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'caravan' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('caravan')}
                  data-testid="button-add-caravan"
                >
                  <Truck className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.caravan")}</span>
                </Button>
              )}
              {availableVehicleTypes.includes('boat') && (
                <Button
                  type="button"
                  variant={addVehicleType === 'boat' ? 'default' : 'outline'}
                  className="h-auto min-h-[70px] py-2 flex-col gap-1"
                  onClick={() => setAddVehicleType('boat')}
                  data-testid="button-add-boat"
                >
                  <Ship className="h-4 w-4" />
                  <span className="text-[10px] leading-tight">{t("booking.boat")}</span>
                </Button>
              )}
            </div>

            {/* Service Selection - Auto adds to cart on select */}
            <MobileSelect
              value=""
              disabled={!addVehicleType}
              onValueChange={(id) => {
                handleServiceSelect(id);
                setShowAddServiceDialog(false);
              }}
              placeholder={addVehicleType ? t("booking.selectService") : t("booking.selectVehicleType")}
              className={cn("rounded-xl h-12", isRtl && "text-right")}
              data-testid="select-add-service"
            >
              {servicesLoading ? (
                <MobileSelectItem value="loading" disabled>{t("booking.loading")}</MobileSelectItem>
              ) : !services || services.length === 0 ? (
                <MobileSelectItem value="no-services" disabled>No services available</MobileSelectItem>
              ) : !addVehicleType ? (
                <MobileSelectItem value="no-vehicle" disabled>{t("booking.selectVehicleType")}</MobileSelectItem>
              ) : (
                services.filter(s => !s.isAddOn).map((service) => {
                  if (!service.vehiclePricing || !service.vehiclePricing[addVehicleType] || service.vehiclePricing[addVehicleType] <= 0) {
                    return null;
                  }
                  return (
                    <MobileSelectItem key={service.id} value={service.id}>
                      {isRtl ? service.nameAr : service.nameEn}
                    </MobileSelectItem>
                  );
                })
              )}
            </MobileSelect>
          </div>

          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto min-w-[140px]"
              onClick={() => setShowAddServiceDialog(false)}
            >
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item-scoped global add-ons dialog — opens when a service is selected
          and global item add-ons exist. Lets the customer pick which to attach. */}
      <Dialog
        open={pendingItemService !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingItemService(null);
            setPendingItemAddOns([]);
            setPendingItemAddOnQty({});
            setEditingCartIndex(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("booking.addOns")}</DialogTitle>
            <DialogDescription>
              {pendingItemService
                ? `${isRtl ? pendingItemService.serviceNameAr : pendingItemService.serviceName} — ${itemAddOnsHint}`
                : itemAddOnsHint}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2 max-h-[45vh] overflow-y-auto">
            {getItemAddOnsFor(pendingItemService?.serviceId).map((addOn) => {
              const checked = pendingItemAddOns.includes(addOn.id);
              const quantity = getAddOnQuantity(pendingItemAddOnQty, addOn.id);
              return (
                // The row is a button, so the stepper sits beside it rather
                // than inside it — nested buttons are invalid.
                <div
                  key={addOn.id}
                  className={cn(
                    "w-full flex items-center gap-2 p-3 rounded-xl border-2 transition-all",
                    checked
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setPendingItemAddOns((prev) =>
                        prev.includes(addOn.id)
                          ? prev.filter((id) => id !== addOn.id)
                          : [...prev, addOn.id],
                      );
                      setPendingItemAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                    }}
                    className="flex-1 min-w-0 flex items-center justify-between gap-2 text-start focus:outline-none"
                    data-testid={`item-addon-${addOn.id}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-medium block">{isRtl ? addOn.nameAr : addOn.nameEn}</span>
                      <span className="text-xs text-muted-foreground">{isRtl ? addOn.descriptionAr : addOn.descriptionEn}</span>
                    </span>
                    <span className="text-sm font-medium text-primary shrink-0">
                      +{(getAddOnPrice(addOn, pendingItemService?.vehicleType) * (checked ? quantity : 1)).toFixed(3)} {t("services.currency")}
                    </span>
                  </button>
                  {addOn.allowQuantity && (
                    <AddOnQuantityStepper
                      quantity={checked ? quantity : 0}
                      onChange={(delta) =>
                        setPendingItemAddOnQty((prev) => stepAddOnQuantity(prev, addOn.id, delta))
                      }
                      onSelect={() => {
                        setPendingItemAddOns((prev) =>
                          prev.includes(addOn.id) ? prev : [...prev, addOn.id],
                        );
                        setPendingItemAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                      }}
                      onDeselect={() => {
                        setPendingItemAddOns((prev) => prev.filter((id) => id !== addOn.id));
                        setPendingItemAddOnQty((prev) => dropAddOnQuantity(prev, addOn.id));
                      }}
                      testId={`item-addon-qty-${addOn.id}`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-2">
            <Button className="flex-1" onClick={confirmItemAddOns}>
              {t("common.continue")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
